import { useState } from "react";

import { AttachFile } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";

import { DeliveryFilesModal } from "components/DeliveryFilesModal";
import { ShipmentStatusEmptyState } from "components/ShipmentStatusEmptyState";
import {
  useCarrierShipmentPageContext,
  useShipmentQuotes,
  useSwrFetch,
} from "hooks";
import {
  carrierHasBookingWriteAccess,
  formatNumberToDecimal,
  getUnitOfMeasureLabel,
  ISOStringToLocalReadableTimestamp,
  shipmentHasBeenDelivered,
} from "utils";
import { getBookedQuoteFromQuotes, getUnitOfMeasureTitle } from "utils/quotes";

import { QUANTITY_REQUIRED_UNIT_OF_MEASURE_TYPES } from "../../../../../../../../constants";

const SECTION_STYLE = {
  borderColor: "#cecdcd",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: 2,
  p: ".5rem",
};

export function ShipmentStatusDisplay() {
  const { shipment } = useCarrierShipmentPageContext();
  const [deliveryFilesOpen, setDeliveryFilesOpen] = useState(false);
  const { quotes, isLoading: quotesLoading } = useShipmentQuotes(
    shipment?.info.id ?? 0,
  );

  const { data: deliveryFilesData, error: deliveryFilesError } =
    useSwrFetch<DeliveryFilesResponse>({
      url:
        shipment && shipmentHasBeenDelivered(shipment)
          ? `/shipments/${shipment.info.id}/delivery-files`
          : null,
      errorMessage: "Failed to load delivery files.",
    });

  const hasDeliveryFiles =
    (deliveryFilesData?.files.length ?? 0) > 0 || deliveryFilesError !== null;

  if (
    !shipment ||
    !carrierHasBookingWriteAccess(shipment) ||
    shipment?.info.type === "MULTI_SHIPMENT_PROJECT"
  ) {
    return null;
  }

  let component = (
    <ShipmentStatusEmptyState subtitle="Mark this shipment as picked up once it has been loaded, then delivered on arrival." />
  );

  if (shipment?.shipment_status === "BOOKING_CANCELLED") {
    if (
      shipment.booking_cancellation !== undefined &&
      shipment.booking_cancellation !== null
    ) {
      component = (
        <Stack direction="column" sx={SECTION_STYLE}>
          <Typography>
            Booking{" "}
            <b>
              <u>cancelled</u>
            </b>{" "}
            by <b>{shipment.booking_cancellation.created_by.user.first_name}</b>{" "}
            <b>{shipment.booking_cancellation.created_by.user.last_name}</b> on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.booking_cancellation.created_at,
            )}
          </Typography>
          <Typography>
            <b>{"Reason: "}</b>
            {shipment.booking_cancellation.reason}
          </Typography>
        </Stack>
      );
    }
  }

  if (shipment?.shipment_status === "DELIVERED") {
    if (shipment.delivery !== undefined && shipment.delivery !== null) {
      const unitOfMeasureSupportsFinalPrice =
        QUANTITY_REQUIRED_UNIT_OF_MEASURE_TYPES.includes(
          shipment.info.quoting_unit_of_measure,
        );
      const hasQuantity = shipment.delivery.unit_of_measure_quantity !== null;

      // Get price from book now info if available, otherwise from booked quote
      let price: number | null = null;
      let currency: string | null = null;

      if (shipment.book_now_info.is_book_now) {
        price = shipment.book_now_info.book_now_price;
        currency = shipment.book_now_info.book_now_currency;
      } else if (!quotesLoading) {
        const bookedQuote = getBookedQuoteFromQuotes(quotes);
        if (bookedQuote) {
          price = bookedQuote.price;
          currency = bookedQuote.currency;
        }
      }

      const quantity = shipment.delivery.unit_of_measure_quantity
        ? parseFloat(
            formatNumberToDecimal(shipment.delivery.unit_of_measure_quantity),
          )
        : 0;
      const finalPrice = price ? (price * quantity).toLocaleString() : "0";

      component = (
        <Stack direction="column" spacing={1} sx={SECTION_STYLE}>
          <Typography>
            Shipment marked as{" "}
            <b>
              <u>delivered</u>
            </b>{" "}
            on{" "}
            {ISOStringToLocalReadableTimestamp(shipment.delivery.delivered_at)}
          </Typography>
          <Typography>
            Marked delivered by{" "}
            <b>{shipment.delivery.created_by.user.first_name}</b>{" "}
            <b>{shipment.delivery.created_by.user.last_name}</b>
          </Typography>
          {unitOfMeasureSupportsFinalPrice &&
            hasQuantity &&
            price &&
            currency && (
              <Stack>
                <Typography>
                  <b>Delivered Quantity: </b>
                  {quantity.toLocaleString()}{" "}
                  {getUnitOfMeasureLabel(shipment.info.quoting_unit_of_measure)}
                </Typography>
                <Typography>
                  <b>Rate: </b>${price.toLocaleString()} {currency} per{" "}
                  {getUnitOfMeasureTitle(shipment.info.quoting_unit_of_measure)}
                </Typography>
                <Typography>
                  <b>Final Price: </b>${finalPrice} {currency}
                </Typography>
              </Stack>
            )}
          {shipment.delivery.notes != null &&
            shipment.delivery.notes !== "" && (
              <Typography>
                <b>{"Notes: "}</b>
                {shipment.delivery.notes}
              </Typography>
            )}
          <Typography variant="body2" color="text.secondary">
            Delivered status created on{" "}
            {ISOStringToLocalReadableTimestamp(shipment.delivery.created_at)}
          </Typography>
          {hasDeliveryFiles && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={() => setDeliveryFilesOpen(true)}
              sx={{ alignSelf: "flex-start", mt: 0.5 }}
            >
              View attachments
            </Button>
          )}
        </Stack>
      );
    }
  }

  if (shipment?.shipment_status === "PICKED_UP") {
    if (shipment.first_pickup !== undefined && shipment.first_pickup !== null) {
      component = (
        <Stack direction="column" spacing={1} sx={SECTION_STYLE}>
          <Typography>
            Shipment marked as{" "}
            <b>
              <u>picked up</u>
            </b>{" "}
            on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.first_pickup.picked_up_at,
            )}
          </Typography>
          <Typography>
            Marked picked up by{" "}
            <b>{shipment.first_pickup.created_by.user.first_name}</b>{" "}
            <b>{shipment.first_pickup.created_by.user.last_name}</b>
          </Typography>
          {shipment.first_pickup.notes != null &&
            shipment.first_pickup.notes !== "" && (
              <Typography>
                <b>{"Notes: "}</b>
                {shipment.first_pickup.notes}
              </Typography>
            )}
          <Typography variant="body2" color="text.secondary">
            Picked up status created on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.first_pickup.created_at,
            )}
          </Typography>
        </Stack>
      );
    }
  }

  return (
    <Stack mt="1rem">
      {component}
      {shipment && shipmentHasBeenDelivered(shipment) && (
        <DeliveryFilesModal
          open={deliveryFilesOpen}
          onClose={() => setDeliveryFilesOpen(false)}
          shipmentId={shipment.info.id}
          preloadedData={deliveryFilesData}
        />
      )}
    </Stack>
  );
}
